import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { showLoading } from "react-global-loading";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import { ErrorNotify } from "../../shared/Alert";
import {
  getChecklistDetail,
  getQuestionlists,
} from "../../helpers/backend_helper";
import "../Standards/Standards.scss";
import "./ChecklistPreview.scss";

const relationNames = (items, relation) =>
  (items || [])
    .map((item) => item?.[relation]?.name || item?.name)
    .filter(Boolean)
    .join(", ") || "—";

const questionStatus = (value) =>
  value === 2 || String(value).toLowerCase() === "archived"
    ? "Archived"
    : value === 0 || String(value).toLowerCase() === "draft"
    ? "Draft"
    : "Active";

const checklistStatus = (value) =>
  value === 2
    ? { label: "Archived", tone: "inactive" }
    : value === 0
    ? { label: "Draft", tone: "inactive" }
    : { label: "Active", tone: "active" };

const serviceCodeCell = (serviceCode) =>
  serviceCode ? (
    <span className="checklist-preview__service-code">
      <b>{serviceCode.name || serviceCode.code || "—"}</b>
      {serviceCode.description && <small>{serviceCode.description}</small>}
    </span>
  ) : (
    "—"
  );

const ChecklistPreview = ({
  checklist: checklistProp = null,
  checklistId,
  onClose,
  modal = false,
}) => {
  const { id: routeChecklistId } = useParams();
  const id = checklistId || routeChecklistId;
  const location = useLocation();
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(
    checklistProp || location.state?.checklist || null
  );
  const [questions, setQuestions] = useState([]);
  const [search, setSearch] = useState("");
  const [area, setArea] = useState("all");
  const [topic, setTopic] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    let isMounted = true;
    showLoading(true);
    Promise.all([getChecklistDetail(id), getQuestionlists(id)])
      .then(([checklistResponse, questionsResponse]) => {
        if (!isMounted) return;
        if (checklistResponse?.status && checklistResponse.data)
          setChecklist(checklistResponse.data);
        if (questionsResponse?.data) setQuestions(questionsResponse.data);
      })
      .catch((error) => {
        console.error(error);
        if (isMounted) ErrorNotify("Unable to load the checklist preview.");
      })
      .finally(() => showLoading(false));
    return () => {
      isMounted = false;
    };
  }, [id]);

  const areas = useMemo(
    () =>
      [
        ...new Set(
          questions.map((item) => item.checklistArea?.name).filter(Boolean)
        ),
      ].sort(),
    [questions]
  );
  const topics = useMemo(
    () =>
      [
        ...new Set(
          questions.map((item) => item.checklistTopic?.name).filter(Boolean)
        ),
      ].sort(),
    [questions]
  );
  const filteredQuestions = useMemo(
    () =>
      questions.filter((item) => {
        const searchable = [
          item.question,
          item.referenceNo,
          item.checklistArea?.name,
          item.checklistTopic?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return (
          searchable.includes(search.toLowerCase()) &&
          (area === "all" || item.checklistArea?.name === area) &&
          (topic === "all" || item.checklistTopic?.name === topic) &&
          (status === "all" ||
            questionStatus(item.status).toLowerCase() === status)
        );
      }),
    [area, questions, search, status, topic]
  );

  const resetFilters = () => {
    setSearch("");
    setArea("all");
    setTopic("all");
    setStatus("all");
  };

  const columns = [
    {
      id: "number",
      header: "#",
      accessorFn: (_item, index) => index + 1,
      minSize: 48,
      maxSize: 48,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <span className="checklist-preview__question-number">
          {questions.findIndex((item) => item.id === row.original.id) + 1}
        </span>
      ),
    },
    {
      id: "question",
      header: "Question",
      accessorKey: "question",
      minSize: 330,
      flex: 2.1,
      cell: ({ getValue }) => (
        <span className="checklist-preview__question-text">
          {getValue() || "Untitled question"}
        </span>
      ),
    },
    {
      id: "reference",
      header: "Reference",
      accessorKey: "referenceNo",
      minSize: 120,
      flex: 0.72,
    },
    {
      id: "area",
      header: "Area",
      accessorFn: (item) => item.checklistArea?.name || "—",
      minSize: 140,
      flex: 0.85,
      cell: ({ getValue }) => (
        <span className="checklist-preview__area-text">{getValue()}</span>
      ),
    },
    {
      id: "topic",
      header: "Topic",
      accessorFn: (item) => item.checklistTopic?.name || "—",
      minSize: 140,
      flex: 0.85,
    },
    {
      id: "serviceCode",
      header: "Service Code",
      accessorFn: (item) =>
        item.serviceCode?.name || item.serviceCode?.code || "—",
      minSize: 140,
      flex: 0.9,
      cell: ({ row }) => serviceCodeCell(row.original.serviceCode),
    },
    {
      id: "mustService",
      header: "Must Service",
      accessorFn: (item) =>
        item.mandatoryServiceCode?.name ||
        item.mandatoryServiceCode?.code ||
        "—",
      minSize: 140,
      flex: 0.9,
      cell: ({ row }) => serviceCodeCell(row.original.mandatoryServiceCode),
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (item) => questionStatus(item.status),
      minSize: 90,
      flex: 0.55,
      cell: ({ getValue }) => (
        <span
          className={`checklist-preview__status checklist-preview__status--${getValue().toLowerCase()}`}
        >
          {getValue()}
        </span>
      ),
    },
  ];

  const checklistName = checklist?.name || "Checklist Preview";
  const previewStatus = checklistStatus(checklist?.status);

  const closePreview = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate("/system/checklist");
  };
  return (
    <main
      className={(`page-content checklist-preview${
        modal ? " checklist-preview--modal" : ""
      }`) + " piers-page"}
    >
      <div className="checklist-preview__topline">
        {!modal && (
          <button
            type="button"
            className="checklist-preview__back"
            onClick={closePreview}
          >
            <i className="bx bx-chevron-left" /> Back to Checklists
          </button>
        )}
        <div className="checklist-preview__top-actions">
          <button
            type="button"
            className="checklist-preview__close-preview"
            onClick={closePreview}
          >
            <i className="bx bx-x" /> Close Preview
          </button>
        </div>
      </div>
      <header className="checklist-preview__heading piers-page-header">
        <h1 className="piers-page-title">Preview Checklist</h1>
        <p>
          Review the checklist details and questions before using it in an
          engagement.
        </p>
      </header>
      <div className="checklist-preview__layout">
        <section className="checklist-preview__content">
          <article className="checklist-preview__overview">
            <span
              className="checklist-preview__overview-icon"
              aria-hidden="true"
            >
              <i className="bx bx-list-check" />
            </span>
            <div className="checklist-preview__overview-title">
              <h2>{checklistName}</h2>
            </div>
            <dl>
              <div>
                <dt>Standard</dt>
                <dd>{checklist?.standardType?.name || "—"}</dd>
              </div>
              <div>
                <dt>Work Types</dt>
                <dd>{relationNames(checklist?.workTypes, "workType")}</dd>
              </div>
              <div>
                <dt>Industry Types</dt>
                <dd>
                  {relationNames(checklist?.industryTypes, "industryType")}
                </dd>
              </div>
              <div>
                <dt>Client Types</dt>
                <dd>{relationNames(checklist?.clientTypes, "clientType")}</dd>
              </div>
              <div>
                <dt>Total Questions</dt>
                <dd>{questions.length}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <span
                    className={`checklist-preview__status checklist-preview__status--${previewStatus.tone}`}
                  >
                    {previewStatus.label}
                  </span>
                </dd>
              </div>
            </dl>
          </article>
          <header className="checklist-preview__questions-heading">
            <h2>Questions</h2>
            <span>
              Browse the checklist questions included in this preview.
            </span>
          </header>
          
          <section className="checklist-preview__grid">
            <CustomDataGrid
 filterContent={<section
            className="checklist-preview__filters"
            aria-label="Preview question filters"
          >
            <label>
              <span>Search</span>
              <div>
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search questions..."
                />
              </div>
            </label>
            <label>
              <span>Area</span>
              <select
                value={area}
                onChange={(event) => setArea(event.target.value)}
              >
                <option value="all">All Areas</option>
                {areas.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </label>
            <label>
              <span>Topic</span>
              <select
                value={topic}
                onChange={(event) => setTopic(event.target.value)}
              >
                <option value="all">All Topics</option>
                {topics.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
            <div className="checklist-preview__filter-action">
              <span aria-hidden="true">Actions</span>
              <button type="button" onClick={resetFilters}>Clear</button>
            </div>
          </section>}

              data={filteredQuestions}
              columns={columns}
              fileName="checklist-preview"
              recordLabel="questions"
              showToolbar={false}
              initialPageSize={15}
              rowHeight={64}
              columnHeaderHeight={48}
              gridSx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#ffffff !important",
                  borderBottom: "1px solid #dfe6f0",
                },
                "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
                  { backgroundColor: "#ffffff !important", color: "#52627f" },
                "& .MuiDataGrid-columnHeaderTitle": {
                  color: "#52627f",
                  fontSize: 11,
                  fontWeight: 700,
                },
                "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
                "& .MuiDataGrid-cell": {
                  alignItems: "center",
                  borderBottomColor: "#edf0f5",
                  color: "#273957",
                  fontSize: 12,
                  whiteSpace: "normal",
                },
                "& .MuiDataGrid-row": { minHeight: "64px !important" },
                "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
                  { borderTopColor: "#e7ebf2", fontSize: 12 },
              }}
            />
          </section>
        </section>
      </div>
    </main>
  );
};

export default ChecklistPreview;
